import { useState, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { useTags } from '../hooks/useTags';
import CitySearch from './CitySearch';
import { getActiveLocationsForDate } from '../utils';

const GlobalFilterBar = ({ 
  visibleCalendars, 
  dispatch, 
  advancedFilters = [],
  isFilterPanelOpen,
  isMapPanelOpen,
  googleCalendars,
  profile,
  saveUserProfile,
  mapStartDate,
  mapEndDate,
  searchRadius,
  customCenter,
  friendFilter,
  selectedTripId,
  allUsers,
  trips = [],
  calendarItems = [],
  authUser
}) => {
  const [openTagPopup, setOpenTagPopup] = useState(null);
  const [selectedLocationMode, setSelectedLocationMode] = useState('custom');
  const { tagsHierarchy, getChildTags, getParentTags } = useTags();

  useEffect(() => {
    if (!customCenter) {
      setSelectedLocationMode('custom');
    }
  }, [customCenter]);

  const upcomingLocations = useMemo(() => {
    const locs = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    (calendarItems || []).forEach(item => {
      if (!['event', 'tentative', 'concert'].includes(item.type)) return;
      const iDate = item.startDate || item.date;
      if (!iDate) return;
      
      const eDate = new Date(iDate + 'T12:00:00');
      if (eDate < today) return;

      const lat = item.addressLat || item.lat;
      const lng = item.addressLng || item.lng;
      
      if (lat && lng) {
        locs.push({
          id: `item-${item.id}`,
          name: `${item.title || item.venueName || 'Event'} (${eDate.toLocaleDateString([], { month: 'short', day: 'numeric' })})`,
          lat,
          lng,
          date: iDate
        });
      }
    });

    locs.sort((a, b) => a.date.localeCompare(b.date));
    return locs;
  }, [calendarItems]);

  const activeLocationNow = useMemo(() => {
    if (customCenter) return customCenter.address || customCenter.name || 'Custom';
    
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    const homeCity = profile?.cities?.[0] || { name: 'Home', lat: 38.9072, lng: -77.0369 };
    
    const locs = getActiveLocationsForDate(dateStr, today, trips || [], calendarItems || [], homeCity);
    if (locs && locs.length > 0) {
      const loc = locs[0];
      if (loc.type === 'event' && loc.id) {
        const item = calendarItems.find(i => i.id === loc.id);
        return item?.address || item?.venueName || item?.title || 'Event';
      }
      if (loc.type === 'home') return homeCity.address || homeCity.name || 'Home';
      if (loc.type === 'trip' && loc.id) {
        const t = trips.find(i => i.id === loc.id);
        return t?.address || t?.city || 'Trip';
      }
      if (loc.type === 'hotel' && loc.id) {
        const h = calendarItems.find(i => i.id === loc.id);
        return h?.address || h?.venueName || h?.title || 'Hotel';
      }
      return 'Current Loc';
    }
    return homeCity.address || homeCity.name || 'Home';
  }, [trips, calendarItems, profile, customCenter]);

  const distToX = (d) => {
    if (d <= 50) return d;
    if (d <= 200) return 50 + (d - 50) / 6;
    return 75 + (d - 200) / 32;
  };

  const xToDist = (x) => {
    if (x <= 50) return Math.max(1, x);
    if (x <= 75) return 50 + (x - 50) * 6;
    return 200 + (x - 75) * 32;
  };

  const checkboxIds = [
    'nytm-event',
    'nytm-tentative',
    'nytm-tasks-all',
    'nytm-tasks-starred',
    'nytm-tasks-h',
    'nytm-tasks-m',
    'nytm-tasks-l',
    'nytm-birthday',
    'nytm-holiday',
    'nytm-flight',
    'nytm-hotel',
  ];
  const radioIds = ['nytm-concert', 'nytm-musicvenue', 'nytm-restaurant', 'nytm-drinks', 'nytm-coffee', 'nytm-sight', 'nytm-shopping', 'nytm-wanttogo'];
  const travelGroupIds = ['nytm-musicvenue', 'nytm-restaurant', 'nytm-drinks', 'nytm-coffee', 'nytm-sight', 'nytm-shopping', 'nytm-wanttogo'];
  
  const visibleList = Array.isArray(visibleCalendars)
    ? visibleCalendars
    : [
        'nytm-event',
        'nytm-tasks-all',
        'nytm-tasks-starred',
        'nytm-tasks-h',
        'nytm-tasks-m',
        'nytm-tasks-l',
        'nytm-birthday',
        'nytm-holiday',
      ];

  const toggleCalendar = (id) => {
    let newList = [...visibleList];
    
    if (newList.includes(id)) {
      newList = newList.filter((x) => x !== id);
    } else {
      newList.push(id);
    }
    
    dispatch({ type: 'SET_CALENDAR_VISIBILITY', payload: newList });
  };

  const handleToggleAdvancedFilter = (id) => {
    let newList = [...advancedFilters];
    if (id.startsWith('tag-')) {
      const rawTag = id.replace('tag-', '');
      if (advancedFilters.includes(id)) {
        newList = newList.filter(f => f !== id);
        const children = getChildTags(rawTag);
        newList = newList.filter(f => !children.includes(f.replace('tag-', '')));
        const parents = getParentTags(rawTag);
        if (parents) {
          parents.forEach(p => {
            newList = newList.filter(f => f !== `tag-${p}`);
          });
        }
      } else {
        newList.push(id);
        const children = getChildTags(rawTag);
        if (children) {
          children.forEach(c => {
            const cid = `tag-${c}`;
            if (!newList.includes(cid)) newList.push(cid);
          });
        }
      }
    } else {
      if (advancedFilters.includes(id)) {
        newList = newList.filter(f => f !== id);
      } else {
        newList.push(id);
      }
    }
    dispatch({ type: 'SET_ADVANCED_FILTERS', payload: newList });
  };

  return (
    <div className="flex items-center gap-2 h-full flex-1 overflow-hidden mr-auto">
        <div className="flex items-center gap-1 shrink-0">
          <input 
            type="date"
            value={mapStartDate || ''}
            onChange={(e) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { mapStartDate: e.target.value } })}
            className="bg-zinc-50 border border-zinc-200 rounded-lg px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 outline-none focus:border-zinc-900 w-[95px]"
          />
          <span className="text-[10px] text-zinc-300 font-black">-</span>
          <input 
            type="date"
            value={mapEndDate || ''}
            onChange={(e) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { mapEndDate: e.target.value } })}
            className="bg-zinc-50 border border-zinc-200 rounded-lg px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 outline-none focus:border-zinc-900 w-[95px]"
          />
        </div>

        <div className="relative group w-[110px] shrink-0">
          <select 
            value={selectedTripId || '1month'}
            onChange={(e) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { selectedTripId: e.target.value } })}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-1.5 py-0.5 text-[10px] font-bold appearance-none outline-none focus:border-zinc-900 transition-colors cursor-pointer truncate pr-5"
          >
            <option value="1month">1 Month</option>
            <option value="2months">2 Months</option>
            <option value="3months">3 Months</option>
            {trips.filter(trip => {
              if (!trip.endDate) return true;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const end = new Date(trip.endDate + 'T12:00:00');
              return end >= today;
            }).map(trip => (
              <option key={trip.id} value={trip.id}>
                {trip.city?.split(',')[0] || 'Trip'}
              </option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        </div>

        <div className="relative group w-[100px] shrink-0">
          <select 
            value={friendFilter || 'all'}
            onChange={(e) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { friendFilter: e.target.value } })}
            className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-1.5 py-0.5 text-[10px] font-bold appearance-none outline-none focus:border-zinc-900 transition-colors cursor-pointer pr-5 truncate"
          >
            <option value="all">All Databases</option>
            <option value="me">My Database</option>
            {(profile?.friends || []).map(friendEmail => {
              const friendUser = (allUsers || []).find(u => u.email?.toLowerCase() === friendEmail.toLowerCase());
              if (!friendUser) return null;
              return <option key={friendUser.id} value={friendUser.id}>{friendUser.name || friendEmail}</option>;
            })}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        </div>

        <div className="flex items-center gap-1 w-[160px] shrink-0">
          <div className="relative flex-1">
            <select 
              value={selectedLocationMode}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedLocationMode(val);
                if (val !== 'custom') {
                  const loc = upcomingLocations.find(l => l.id === val);
                  if (loc) dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: { lat: loc.lat, lng: loc.lng, name: loc.name } } });
                } else {
                  dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: null } });
                }
              }}
              className="w-full bg-zinc-50 border border-zinc-200 rounded-lg px-1.5 py-0.5 text-[10px] font-bold appearance-none outline-none focus:border-zinc-900 transition-colors cursor-pointer pr-5 truncate"
            >
              <option value="custom">Custom Location</option>
              {upcomingLocations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          </div>

          <div className={`w-[140px] relative transition-opacity duration-200 ${selectedLocationMode !== 'custom' ? 'opacity-40 pointer-events-none grayscale' : ''}`}>
            <CitySearch
              city={customCenter}
              onCityChange={(c) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: c } })}
              placeholder="Search city..."
              types={['geocode']}
              minimal={true}
              dropdownAlign="right"
              className="w-full text-[10px] py-1 px-2 border-zinc-200 bg-zinc-50"
            />
            {customCenter && selectedLocationMode === 'custom' && (
              <button
                onClick={() => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: null } })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] uppercase font-bold tracking-wider text-red-500 hover:text-red-700 transition-colors bg-zinc-50 px-1"
              >
                X
              </button>
            )}
          </div>
          <button
            onClick={() => {
              if (!navigator.geolocation) {
                alert("Geolocation is not supported by your browser.");
                return;
              }
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  const { latitude, longitude } = position.coords;
                  if (window.google?.maps?.Geocoder) {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
                      if (status === "OK" && results[0]) {
                        const addressComponents = results[0].address_components;
                        let city = "Current Location";
                        const locality = addressComponents.find(c => c.types.includes("locality"));
                        const neighborhood = addressComponents.find(c => c.types.includes("neighborhood"));
                        if (locality) city = locality.short_name;
                        else if (neighborhood) city = neighborhood.short_name;
                        dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: { lat: latitude, lng: longitude, name: city, address: results[0].formatted_address } } });
                        setSelectedLocationMode('custom');
                      } else {
                        dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: { lat: latitude, lng: longitude, name: 'Current Location' } } });
                        setSelectedLocationMode('custom');
                      }
                    });
                  } else {
                    dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: { lat: latitude, lng: longitude, name: 'Current Location' } } });
                    setSelectedLocationMode('custom');
                  }
                },
                (error) => alert("Unable to retrieve your location.")
              );
            }}
            className="flex items-center gap-1 shrink-0 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg px-2 py-0.5 hover:bg-blue-100 transition-colors active:scale-95"
            title="Set to Current GPS Location"
          >
            <MapPin size={12} strokeWidth={2.5} />
            <span className="text-[9px] font-bold tracking-wider uppercase">GPS</span>
          </button>
        </div>

        <div className="flex items-center gap-2 shrink-0 bg-zinc-50 border border-zinc-200 rounded-lg px-2 py-0.5" title={activeLocationNow}>
          <MapPin size={12} className="text-zinc-400" />
          <span className="text-[9px] font-bold text-zinc-600 truncate max-w-[180px]">
            {activeLocationNow}
          </span>
          <div className="w-px h-3 bg-zinc-300 mx-1"></div>
          <div className="flex items-center whitespace-nowrap shrink-0 ml-1">
            <input
              type="number"
              min="0"
              max="9999"
              step="10"
              value={searchRadius === '' ? '' : (searchRadius ?? 50)}
              onChange={(e) => {
                const val = e.target.value;
                dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { searchRadius: val === '' ? '' : Number(val) } });
              }}
              className="w-7 p-0 text-sm font-black text-right bg-transparent border-none focus:outline-none text-blue-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-xs text-zinc-400 font-medium mx-1">mi</span>
            <div className="flex flex-col border-l border-zinc-200 pl-1">
               <button 
                 onClick={() => {
                   const current = searchRadius === '' ? 0 : (searchRadius ?? 50);
                   dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { searchRadius: current + 10 } });
                 }}
                 className="text-zinc-400 hover:text-blue-600 hover:bg-zinc-100 rounded-sm leading-none"
               >
                 <ChevronUp size={14} strokeWidth={3} />
               </button>
               <button 
                 onClick={() => {
                   const current = searchRadius === '' ? 0 : (searchRadius ?? 50);
                   dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { searchRadius: Math.max(0, current - 10) } });
                 }}
                 className="text-zinc-400 hover:text-blue-600 hover:bg-zinc-100 rounded-sm leading-none"
               >
                 <ChevronDown size={14} strokeWidth={3} />
               </button>
            </div>
          </div>
        </div>
    </div>
  );
};

export default GlobalFilterBar;
